<template>
    <EChart
        :options="chartOptions"
        card
        :header="$t('averageAvailabilityByProducts')"
        overlay
        :loading="request.getLoading() || requestCompare.getLoading()"
        :error="request.getError()"
    >
        <template #nav>
            <div class="d-flex gap-3">
                <b-dropdown :text="`${$t('show')}: ${$t(`filterQuantity${pascalCase(String(filterQuantity))}`)}`" toggle-class="caret" :disabled="request.getLoading()">
                    <template v-for="item in filterQuantityOptions">
                        <b-dropdown-item :key="item" :active="item === filterQuantity" @click="() => filterQuantity = item">{{ $t(`filterQuantity${pascalCase(String(item))}`) }}</b-dropdown-item>
                    </template>
                </b-dropdown>
                <b-dropdown :text="`${$t('sortBy')}: ${$t(`sortBy${pascalCase(String(sortBy))}`)}`" toggle-class="caret" :disabled="request.getLoading()">
                    <template v-for="item in sortByOptions">
                        <b-dropdown-item :key="item" :active="item === sortBy" @click="() => sortBy = item">{{ $t(`sortBy${pascalCase(String(item))}`) }}</b-dropdown-item>
                    </template>
                </b-dropdown>
            </div>
        </template>
    </EChart>
</template>



<script>
    import sum from 'lodash/sum';
    import camelCase from 'camelcase';
    import { calcPercentDiff } from '@/utils/number';
    import EChart from '@/components/common/EChart.vue';

    export default {
        name: 'AverageAvailabilityByProducts',
        components: {
            EChart,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.parentComponent.requestData,
                requestCompare: this.parentComponent.requestDataCompare,
                filterQuantity: 10,
                sortBy: 'summary-desc',
            };
        },
        computed: {
            filterQuantityOptions() {
                return [10, 20, 50, null];
            },
            sortByOptions() {
                return ['summary-desc', 'summary-asc', 'change-desc', 'change-asc'];
            },
            data() {
                return this.request.getData() || {};
            },
            dataCompare() {
                return this.requestCompare.getData() || {};
            },
            chartData() {
                return this.computeChartData(this.data);
            },
            chartDataCompare() {
                return this.computeChartData(this.dataCompare);
            },
            sortedChartData() {
                const compareDict = {};
                this.chartDataCompare.forEach(item => {
                    compareDict[item.product] = item.avgAvailability;
                });
                const dataWithDiff = this.chartData.map(item => {
                    const compareValue = compareDict[item.product] || 0;
                    const diff = calcPercentDiff(item.avgAvailability, compareValue);
                    return { ...item, diff };
                });
                let sorted;
                switch (this.sortBy) {
                    case 'summary-desc':
                        sorted = [...dataWithDiff].sort((a, b) => b.avgAvailability - a.avgAvailability);
                        break;
                    case 'summary-asc':
                        sorted = [...dataWithDiff].sort((a, b) => a.avgAvailability - b.avgAvailability);
                        break;
                    case 'change-desc':
                        sorted = [...dataWithDiff].sort((a, b) => b.diff - a.diff);
                        break;
                    case 'change-asc':
                        sorted = [...dataWithDiff].sort((a, b) => a.diff - b.diff);
                        break;
                    default:
                        sorted = dataWithDiff;
                }
                return this.filterQuantity ? sorted.slice(0, this.filterQuantity) : sorted;
            },
            chartOptions() {
                // guard na brak danych
                if (!this.sortedChartData.length) {
                    return { series: [] };
                }

                // przygotowanie tablic
                const names = this.sortedChartData.map(item => item.product);
                const values = this.sortedChartData.map(item => item.avgAvailability);
                const diffs = this.sortedChartData.map(item => item.diff);
                const summary = sum(values);

                return {
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: { type: 'shadow' },
                    },
                    xAxis: {
                        type: 'value',
                        max: 'dataMax',
                    },
                    yAxis: [
                        // lewa oś z nazwami produktów
                        {
                            type: 'category',
                            inverse: true,
                            data: names,
                        },
                        // prawa oś z procentową zmianą
                        {
                            type: 'category',
                            inverse: true,
                            data: names,
                            position: 'right',
                            axisLine: { show: false },
                            axisTick: { show: false },
                            axisLabel: {
                                rich: {
                                    pos: {
                                        color: 'green',
                                        backgroundColor: 'rgba(0,255,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    zero: {
                                        color: '#666',
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    neg: {
                                        color: 'red',
                                        backgroundColor: 'rgba(255,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                },
                                formatter: (value, idx) => {
                                    const d = diffs[idx];
                                    const pct = this.$format.percent(d);
                                    if (d > 0) return `{pos|▲${pct}}`;
                                    if (d < 0) return `{neg|▼${pct}}`;
                                    return `{zero|${pct}}`;
                                },
                            },
                        },
                    ],
                    series: [
                        {
                            type: 'bar',
                            stack: 'total',
                            // etykieta wewnątrz słupka: udział % + wartość
                            label: {
                                show: true,
                                position: 'insideLeft',
                                formatter: params => {
                                    const v = params.data.value;
                                    return `${this.$format.percent((v * 100) / summary, 2)} (${v})`;
                                },
                            },
                            data: values.map(v => ({ value: v })),
                        },
                    ],
                };
            },
        },
        methods: {
            pascalCase(text) {
                return camelCase(text, { pascalCase: true });
            },
            computeChartData(source) {
                /* eslint-disable no-restricted-syntax, guard-for-in */
                const eventsByProduct = {};
                Object.values(source).forEach(productObj => {
                    const productName = productObj.product.name;
                    if (!eventsByProduct[productName]) {
                        eventsByProduct[productName] = [];
                    }
                    const charts = productObj.charts;
                    Object.keys(charts).forEach(chartKey => {
                        charts[chartKey].forEach(chartObj => {
                            const segments = chartObj.chart.segments;
                            if (segments && segments.length) {
                                segments.forEach(segment => {
                                    eventsByProduct[productName].push({ time: segment.start, delta: +1 });
                                    eventsByProduct[productName].push({ time: segment.end, delta: -1 });
                                });
                            }
                        });
                    });
                });
                const productSummaries = [];
                for (const product in eventsByProduct) {
                    const events = eventsByProduct[product];
                    const grouped = {};
                    events.forEach(e => {
                        grouped[e.time] = (grouped[e.time] || 0) + e.delta;
                    });
                    const times = Object.keys(grouped)
                        .map(t => Number(t))
                        .sort((a, b) => a - b);
                    if (times.length === 0) continue; // eslint-disable-line no-continue
                    let cumulative = 0;
                    let weightedSum = 0;
                    let totalDuration = 0;
                    let prevTime = times[0];
                    times.forEach(time => {
                        const duration = time - prevTime;
                        weightedSum += cumulative * duration;
                        totalDuration += duration;
                        cumulative += grouped[time];
                        prevTime = time;
                    });
                    const avg = totalDuration ? weightedSum / totalDuration : cumulative;
                    productSummaries.push({
                        product,
                        avgAvailability: Math.round(avg),
                    });
                }
                productSummaries.sort((a, b) => b.avgAvailability - a.avgAvailability);
                return productSummaries;
                /* eslint-enable no-restricted-syntax, guard-for-in */
            },
        },
    };
</script>



<i18n lang="json" locale="pl" scope="local">
{
    "filterQuantity10": "10",
    "filterQuantity20": "20",
    "filterQuantity50": "50",
    "filterQuantityNull": "wszystkie",
    "sortBySummaryDesc": "Średnia % malejąco",
    "sortBySummaryAsc": "Średnia % rosnąco",
    "sortByChangeDesc": "Zmiana % (do poprzedniego zakresu) malejąco",
    "sortByChangeAsc": "Zmiana % (do poprzedniego zakresu) rosnąco"
}
</i18n>

<i18n lang="json" locale="en" scope="local">
{
    "filterQuantity10": "10",
    "filterQuantity20": "20",
    "filterQuantity50": "50",
    "filterQuantityNull": "all",
    "sortBySummaryDesc": "Average % descending",
    "sortBySummaryAsc": "Average % ascending",
    "sortByChangeDesc": "Change % (to previous range) descending",
    "sortByChangeAsc": "Change % (to previous range) ascending"
}
</i18n>
